import { useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'lead_email';

export function getSavedLeadEmail() {
  try { return localStorage.getItem(STORAGE_KEY) || ''; } catch (e) { return ''; }
}

export default function LeadGateModal({ open, onClose, onCaptured, templateTitle, templateId }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setEmail(getSavedLeadEmail());
      setErr('');
    }
  }, [open]);

  if (!open) return null;

  const validate = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e?.preventDefault?.();
    const val = email.trim().toLowerCase();
    if (!validate(val)) {
      setErr('Please enter a valid email.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await axios.post(`${API}/leads`, {
        email: val,
        document_name: templateTitle,
        template_id: templateId,
      });
      try { localStorage.setItem(STORAGE_KEY, val); } catch (e) {}
      onCaptured?.(val);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Something went wrong. Try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-stone-900/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
      data-testid="lead-gate-overlay"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-gate-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-stone-400 hover:text-stone-700"
          data-testid="lead-gate-close"
          aria-label="Close"
        >
          <Icons.X size={18} />
        </button>

        <div className="w-11 h-11 rounded-2xl bg-orange-100 text-orange-700 grid place-items-center mb-4">
          <Icons.Mail size={20} />
        </div>
        <h2 className="font-display font-bold text-2xl tracking-tight">
          One quick step
        </h2>
        <p className="text-sm text-stone-500 mt-2">
          Enter your email to download your free {templateTitle}. We'll send occasional tips on
          faster docs — unsubscribe anytime.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <input
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            data-testid="lead-gate-email-input"
            className="w-full px-4 py-3 rounded-2xl border border-stone-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none text-sm"
            disabled={busy}
          />
          {err && (
            <div className="text-xs text-red-600" data-testid="lead-gate-error">{err}</div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="lead-gate-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium btn-clay disabled:opacity-60"
          >
            {busy ? <Icons.Loader2 size={16} className="animate-spin" /> : <Icons.Download size={16} />}
            {busy ? 'Preparing…' : 'Email me the PDF'}
          </button>
          <p className="text-[11px] text-stone-400 text-center">
            No spam. We hate that too.
          </p>
        </form>
      </div>
    </div>
  );
}
